import baseApi, { TENANT_IDENTIFIER, fileUploadBaseQuery } from './base';
import {
  ElearningPulishReviewResult,
  ElearningTag,
  MyElearningTag,
  MyElearningDetailsTag,
} from './tags';
import {
  ChannelsPublishDateType,
  ElearningDataType,
  PaginationParams,
  PaginationType,
  PublishElearningType,
  StatusMetadataType,
} from '@/modules/types/elearning';
import {
  getAssetLocation,
  getEntityLocation,
  transformElearningThemeAssetLinks,
} from '@/lib/util';
import config from '@/lib/config/index';

export interface ElearningCreateResponse {
  id: string;
  name: string;
}

export interface ElearningCreatePayload {
  name: string;
  tags: string;
}

export interface ElearningDetails {
  details: {
    totalFrames: number;
  };
  statusMetadata: StatusMetadataType;
  date: {
    modified: Date;
    created: Date;
    publishedLive: Date | null;
    publishedReview: Date | null;
    channels?: ChannelsPublishDateType;
  };
  lastModifiedDate: Date;
}

export interface FileCompletionDto {
  prompt: string;
}

export interface AIGenerationResponse {
  elearningId: string;
}

const myElearningsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    generateElearningFromFile: build.mutation<
      AIGenerationResponse,
      { prompt: string; file: File }
    >({
      queryFn: async ({ prompt, file }, api, extraOptions) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('userInput', prompt);

        // Use the dedicated file upload base query
        const result = await fileUploadBaseQuery(
          {
            url: 'ai-content/elearning-generation',
            method: 'POST',
            body: formData,
          },
          api,
          extraOptions,
        );

        return result as any;
      },
      invalidatesTags: [ElearningTag, MyElearningTag],
    }),
    getMyLibrary: build.query<
      PaginationType<ElearningDataType>,
      void | PaginationParams
    >({
      query: (params) =>
        params
          ? {
              url: `elearnings`,
              params,
            }
          : { url: `elearnings` },
      providesTags: [MyElearningTag],
    }),
    getElearning: build.query<ElearningDataType, string>({
      query: (elearningId) => `elearnings/${elearningId}`,
      transformResponse: (response: ElearningDataType) => {
        // @TODO: consolidate w/ all entities & move to use Theme style - since that is the hook that applies this
        if (response.theme) {
          const themeLocation = getEntityLocation({
            tenantId: response.tenantId,
            entity: 'themes',
            entityId: response.theme.id,
          });

          const transformedTheme = transformElearningThemeAssetLinks(
            response.theme,
            `${themeLocation}/pages/images`,
          );

          response.theme = transformedTheme;
        }

        return response;
      },
      providesTags: [ElearningTag],
    }),
    createElearning: build.mutation<
      ElearningCreateResponse,
      Partial<ElearningCreatePayload>
    >({
      query(post) {
        return {
          url: 'elearnings',
          method: 'POST',
          body: post,
          responseType: 'json',
        };
      },
      invalidatesTags: [ElearningTag, MyElearningTag],
    }),
    updateElearning: build.mutation<
      ElearningCreateResponse,
      Partial<ElearningDataType>
    >({
      query: (elearning) => ({
        url: `elearnings/${elearning.id}`,
        method: 'PUT',
        body: elearning,
      }),
      invalidatesTags: [ElearningTag, MyElearningDetailsTag, MyElearningTag],
    }),
    deleteElearning: build.mutation<any, string>({
      query: (elearningId) => ({
        url: `elearnings/${elearningId}`,
        method: 'PUT',
        body: { isDeleted: true },
      }),
      invalidatesTags: [ElearningTag, MyElearningTag, MyElearningDetailsTag],
    }),
    publishElearning: build.mutation<
      PublishElearningType,
      Partial<PublishElearningType>
    >({
      query: (post) => ({
        url: 'elearnings/publish',
        method: 'POST',
        body: post,
        responseType: 'json',
      }),
      invalidatesTags: [
        { type: ElearningPulishReviewResult },
        MyElearningDetailsTag,
        MyElearningTag,
      ],
    }),
    getPublishResult: build.query<any, any>({
      query: ({ elearningId, type }) => ({
        url: `elearnings/publish/result/${elearningId}/${type}`,
      }),
      providesTags: [ElearningPulishReviewResult],
    }),
    getElearningDetails: build.query<ElearningDetails, string>({
      query: (id) => ({
        url: `elearnings/${id}/details`,
        method: 'GET',
      }),
      providesTags: [MyElearningDetailsTag],
    }),
    duplicateElearning: build.mutation<any, string>({
      query: (elearningId) => ({
        url: `elearnings/duplicate/${elearningId}`,
        method: 'POST',
      }),
      invalidatesTags: [ElearningTag, MyElearningTag, MyElearningDetailsTag],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGenerateElearningFromFileMutation,
  useGetMyLibraryQuery,
  useGetElearningQuery,
  useCreateElearningMutation,
  useUpdateElearningMutation,
  usePublishElearningMutation,
  useGetPublishResultQuery,
  useDeleteElearningMutation,
  useGetElearningDetailsQuery,
  useDuplicateElearningMutation,
} = myElearningsApi;

export default myElearningsApi;
